import { useState } from "react";
import { Helmet } from "react-helmet-async";
import ContactUs from "../components/ContactUs";
import Footer from "../components/Footer";
import Header from "../components/Header";
import "../Style.css";
import "./WorkshopsAndSeminars.css";

export default function WorkshopsAndSeminarsPage() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

  const toggleContactModal = () => {
    setIsContactModalOpen(!isContactModalOpen);
  };

  const toggleNotifyModal = () => {
    setIsNotifyModalOpen(!isNotifyModalOpen);
  };

  const seminars = [
    {
      title: "All About Credit",
      description:
        "Participants will gain a clear understanding of Credit Profile Basics, including the roles of the three major bureaus—Experian, TransUnion, and Equifax. You will learn how credit works and why a strong score is the key to better loan terms and lower interest rate. We also demystify the FICO and VantageScore grading systems, showing you precisely what lenders assess when they review your history.",
      keyTakeaways: [
        "Strategic Management: Learn to maintain a healthy profile by keeping debt low and inquiries to a minimum.",
        "Credit Recovery: Guidance on addressing errors, negotiating settlements, and utilizing credit repair services.",
        'Loans & Debt: Discover the "Best Way to Manage a Credit Card" to ensure it works for you, not the lender.',
        "Debit vs. Credit: Understand the unique protections and risks of each, including the benefits of secured cards for building history.",
      ],
    },
    {
      title: "Savings, Expenses, and Budget",
      description:
        'Participants will learn to transform financial stress into long-term security through proactive budgeting and strategic saving. The session covers essential components of financial health, including building a six-month emergency fund, differentiating between survival "needs" and non-essential "wants," and utilizing automated accounts to ensure bills are paid and savings grow.',
      keyTakeaways: [
        'Core pillars of financial health: Building a six-month emergency fund, Distinguishing essential "needs" from discretionary "wants", Using automation to manage bill payments and grow savings consistently',
        "Expense management strategies: Negotiating rent and recurring expenses, Refinancing debt to reduce interest costs, Evaluating the true, long-term cost of major purchases such as vehicles and homes",
        "Assess financial health: Debt Service Ratio, Housing Expense to Income Ratio",
      ],
    },
    {
      title: "Loans and Debt",
      description:
        'This session is designed to transform debt from a source of stress into a manageable financial tool. The presentation begins by demystifying the core components of any loan, including principal balances, interest rates, and the critical concept of compounding interest—often described as "interest paid on interest". Participants will explore the nuances of "Good Debt"—loans used for assets that appreciate or create revenue, such as mortgages and education—versus "Bad Debt," which covers depreciating items like credit cards and payday loans.',
      keyTakeaways: [
        "Loan Qualification: Master the step-by-step process of pre-qualification and shopping for the best rates",
        "Risk Assessment: Learn to calculate your debt-to-income ratio to ensure your total debt payments remain at a safe level.",
        "Debt reduction: Compare the Debt Avalanche method with the Debt Snowball method to being paying off your debt.",
      ],
    },
  ];

  const weeklyTopics = [
    "Introduction & Financial Psychology: Explore money mindset and financial behaviors",
    "Understanding Credit: Learn how credit profiles, scores, and credit cards work",
    "Loans & Debt Management: Understand common loan types and effective repayment strategies",
    "Savings, Expenses & Budgeting: Learn approaches to consistent saving and managing monthly cash flow",
    "Taking Action: Apply concepts to your selected financial goal",
    "Wrap-Up & Progress Review: Refine your action plan and identify next steps for continued success",
  ];

  return (
    <>
      <Helmet>
        <title>
          Financial Literacy Workshops for Employees | Corporate Wellness
          Seminars | Own Your Money
        </title>
        <meta
          name="description"
          content="Financial literacy workshops for employees and corporate wellness seminars. Expert financial wellness consultant offering customizable financial education programs for small businesses, nonprofits, and community organizations in Holly Springs NC."
        />
        <meta
          name="keywords"
          content="financial literacy workshops for employees, corporate wellness seminars, financial wellness consultant for small business, employee financial education programs, workplace financial wellness, financial coaching workshops, money management seminars, Holly Springs NC business workshops"
        />
        <link
          rel="canonical"
          href="https://www.ownyourmoney.biz/workshops-seminars"
        />
        <meta
          property="og:title"
          content="Financial Workshops & Seminars | Own Your Money"
        />
        <meta
          property="og:description"
          content="Empower your community with interactive financial wellness seminars. Customizable workshops for businesses, nonprofits, and organizations."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.ownyourmoney.biz/workshops-seminars"
        />
        <meta
          property="og:image"
          content="https://www.ownyourmoney.biz/og-image.png"
        />
        <meta
          name="twitter:title"
          content="Financial Workshops & Seminars | Own Your Money"
        />
        <meta
          name="twitter:description"
          content="Interactive financial wellness seminars for your organization. Custom educational workshops available."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:image"
          content="https://www.ownyourmoney.biz/og-image.png"
        />
        <meta name="geo.region" content="US-NC" />
        <meta name="geo.placename" content="Holly Springs" />
        <meta name="geo.position" content="35.6518;-78.8336" />
        <meta name="ICBM" content="35.6518, -78.8336" />
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FinancialService",
      "name": "Own Your Money - Financial Workshops & Seminars",
      "description": "Interactive financial wellness seminars and workshops for businesses, nonprofits, and community organizations in Holly Springs NC.",
      "provider": {
        "@type": "Person",
        "name": "Wendy Toh",
        "jobTitle": "Certified Personal Financial Wellness Consultant",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Holly Springs",
          "addressRegion": "NC",
          "addressCountry": "US"
        }
      },
      "areaServed": {
        "@type": "Country",
        "name": "United States"
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Financial Education Services",
        "itemListElement": [
          {
            "@type": "EducationalOrganization",
            "position": 1,
            "name": "All About Credit Workshop",
            "description": "Interactive workshop covering credit profiles, scores, and strategic credit management"
          },
          {
            "@type": "EducationalOrganization",
            "position": 2,
            "name": "Savings, Expenses & Budget Workshop",
            "description": "Learn proactive budgeting and strategic saving techniques"
          },
          {
            "@type": "EducationalOrganization",
            "position": 3,
            "name": "Loans and Debt Workshop",
            "description": "Transform debt management with strategic loan qualification and repayment methods"
          },
          {
            "@type": "EducationalOrganization",
            "position": 4,
            "name": "Financial Foundations Series",
            "description": "Six-week comprehensive workshop program for long-term financial stability"
          }
        ]
      },
      "serviceType": [
        "Financial Education",
        "Corporate Training",
        "Community Workshops"
      ],
      "priceRange": "$$"
    }
    `}
        </script>
      </Helmet>

      <Header />
      <main className="workshops-main">
        <div className="workshops-container">
          <section className="workshops-header">
            <h1>Workshops & Seminars</h1>
            <div className="hero-content">
              <h2>Empower Your Community with Financial Wellness</h2>
              <p>
                Financial stress impacts productivity, health, and overall
                well-being. Wendy Toh, a Certified Personal Financial Wellness
                Consultant, offers customizable, interactive seminars designed
                to bridge the financial literacy gap.
              </p>
              <p>
                Whether you are looking to support your staff, provide added
                value to your clients, or deliver impactful programming to your
                community, these sessions provide actionable tools for long-term
                financial success.
              </p>
            </div>
          </section>

          <section className="expertise-section">
            <h2>Expertise Tailored for Your Organization</h2>
            <div className="expertise-grid">
              <div className="expertise-card">
                <h3>Small Business Owners</h3>
                <p>
                  Offer a high-value "lunch and learn" to increase employee
                  retention and reduce financial stress in the workplace.
                </p>
              </div>
              <div className="expertise-card">
                <h3>Non-Profits</h3>
                <p>
                  Enhance your mission by providing constituents with the
                  literacy needed to build sustainable futures.
                </p>
              </div>
              <div className="expertise-card">
                <h3>Community Agencies</h3>
                <p>
                  Deliver practical, engaging programming that meets the
                  immediate needs of your local residents.
                </p>
              </div>
            </div>
          </section>

          <section className="format-section">
            <h2>Seminar Format</h2>
            <p>
              Each session is designed to be engaging, accessible, and
              practical:
            </p>
            <ul className="format-list">
              <li>
                <strong>Duration:</strong> 75-minute interactive workshops.
              </li>
              <li>
                <strong>Delivery:</strong> Available both online or in person.
              </li>
              <li>
                <strong>Customization:</strong> Content can be tailored to the
                specific demographics and needs of your group.
              </li>
            </ul>
          </section>

          <section className="topics-section">
            <h2>Available Topics Include:</h2>
            <div className="seminars-grid">
              {seminars.map((seminar, index) => (
                <div key={index} className="seminar-card">
                  <h3>{seminar.title}</h3>
                  <p>{seminar.description}</p>
                  <div className="key-takeaways">
                    <h4>Key takeaways include:</h4>
                    <ul>
                      {seminar.keyTakeaways.map((takeaway, takeawayIndex) => (
                        <li key={takeawayIndex}>{takeaway}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="investment-section">
            <h2>Investment</h2>
            <div className="investment-card">
              <div className="price">$300 per seminar</div>
              <p>
                An affordable investment in the long-term financial health of
                your staff, customers, or constituents.
              </p>
              <button
                type="button"
                onClick={toggleContactModal}
                className="schedule-btn primary-btn"
              >
                Schedule a Consultation
              </button>
            </div>
          </section>

          <section className="foundations-section coming-soon">
            <div className="foundations-header">
              <h2>Financial Foundations Series</h2>
              <div className="coming-soon-badge">** Coming soon! **</div>
            </div>

            <div className="foundations-content">
              <div className="foundations-details">
                <p>
                  <strong>Enrollment Capacity:</strong> Minimum 6 participants /
                  Maximum 12 participants
                </p>

                <h3>Program Goals & Objectives</h3>
                <p>
                  This six-week workshop is designed for individuals who want to
                  take meaningful steps toward long-term financial stability and
                  confidence.
                </p>
                <p>
                  Whether participants are seeking to strengthen their financial
                  knowledge, shift unhelpful money habits, or make progress
                  toward a specific financial goal, this program provides
                  practical structure, expert guidance, and peer support.
                </p>
                <p>
                  Through hands-on activities and facilitated discussion,
                  participants will develop insight into their financial
                  behaviors and create a personalized action plan they can begin
                  implementing immediately.
                </p>

                <ul className="objectives-list">
                  <li>
                    Expand financial knowledge and skills across key areas
                    including credit, debt, savings, and budgeting
                  </li>
                  <li>
                    Identify helpful and unhelpful financial behaviors and learn
                    strategies to create positive change
                  </li>
                  <li>
                    Develop a personalized action plan focused on one core
                    financial goal (e.g., reducing debt, creating a budget,
                    saving for a home, building an emergency fund, or planning
                    for retirement)
                  </li>
                  <li>
                    Gain accountability and encouragement from a small cohort of
                    peers working toward similar financial goals
                  </li>
                </ul>

                <h3>Weekly Session Topics</h3>
                <ul className="weekly-topics">
                  {weeklyTopics.map((topic, index) => (
                    <li key={index}>{topic}</li>
                  ))}
                </ul>
              </div>

              <div className="notify-section">
                <p>
                  Be the first to know when this comprehensive program launches!
                </p>
                <button
                  type="button"
                  onClick={toggleNotifyModal}
                  className="notify-btn"
                >
                  Register for Updates
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />

      {isContactModalOpen && (
        <ContactUs onClose={() => setIsContactModalOpen(false)} />
      )}

      {isNotifyModalOpen && (
        <NotifyModal onClose={() => setIsNotifyModalOpen(false)} />
      )}
    </>
  );
}

// Notify Modal Component for Financial Foundations
function NotifyModal({ onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    interest: "",
  });
  const [status, setStatus] = useState("");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("submitting");

    const encode = (data) => {
      return Object.keys(data)
        .map(
          (key) =>
            encodeURIComponent(key) + "=" + encodeURIComponent(data[key]),
        )
        .join("&");
    };

    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "Financial Foundation Workshop Series Inquiry",
          ...formData,
        }),
      });
      setStatus("success");
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      setStatus("error");
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-content notify-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-button" onClick={onClose}>
          ×
        </button>
        <h2>Get Notified: Financial Foundations Series</h2>
        <p>
          Be the first to know when our comprehensive 6-week program becomes
          available!
        </p>

        <form
          name="Financial Foundation Workshop Series Inquiry"
          onSubmit={handleSubmit}
          netlify-honeypot="bot-field"
          data-netlify="true"
        >
          <input
            type="hidden"
            name="form-name"
            value="Financial Foundation Workshop Series Inquiry"
          />
          <p style={{ display: "none" }}>
            <label>
              Don't fill this out if you're human: <input name="bot-field" />
            </label>
          </p>

          <div className="form-group">
            <label htmlFor="name">Full Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email Address *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="interest">
              What interests you most about this program?
            </label>
            <textarea
              id="interest"
              name="interest"
              value={formData.interest}
              onChange={handleInputChange}
              rows="3"
              placeholder="Let us know what specific financial goals you're hoping to achieve..."
            />
          </div>

          <p className="disclosure-text">
            By clicking 'Notify Me,' you agree to receive email updates
            specifically regarding the Financial Foundations Series. Your data
            is safe with us, and you can opt out of these updates at any time.
            View our{" "}
            <a href="/terms" target="_blank" rel="noopener noreferrer">
              Terms
            </a>{" "}
            and{" "}
            <a href="/policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            .
          </p>

          <button
            type="submit"
            className="submit-btn primary-btn"
            disabled={status === "submitting"}
          >
            {status === "submitting" ? "Submitting..." : "Notify Me"}
          </button>

          {status === "success" && (
            <p className="success-message">
              Thank you! We'll notify you when enrollment opens.
            </p>
          )}
          {status === "error" && (
            <p className="error-message">
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
